// PoseLandmarker wrapper.
//
// Sub-agent (P1.3) contract:
//   const tracker = new PoseTracker({ camera });
//   await tracker.load();
//   tracker.start();
//   const f = tracker.lastFrame;
//
// Pipeline: camera.onFrame → detectForVideo(video, perf.now()) → 33 BlazePose
// landmarks in canvas pixels → derived signals (shoulderTilt, squatLevel,
// jumpEvent, armRaise*, motionDelta).
//
// Indices (BlazePose):
//   nose=0  shoulders=11/12  elbows=13/14  wrists=15/16
//   hips=23/24  knees=25/26  ankles=27/28

import { emptyPose, type Point, type PoseFrame } from '@pose-game/core';
import {
  FilesetResolver,
  PoseLandmarker,
  type NormalizedLandmark,
} from '@mediapipe/tasks-vision';
import type { Camera } from './camera.js';

export interface PoseTrackerOptions {
  camera: Camera;
  modelAssetPath?: string;
  wasmBasePath?: string;
}

const DEFAULT_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/latest/pose_landmarker_lite.task';
const DEFAULT_WASM_BASE =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10/wasm';

const BASELINE_CAPTURE_MS = 1500;
const JUMP_RISE_THRESHOLD_RATIO = 0.05; // x shoulder_width
const JUMP_MAX_AIRBORNE_MS = 400;
const ARM_RAISE_MARGIN_RATIO = 0.1;

// 11/12 shoulders, 23/24 hips, 27/28 ankles, 15/16 wrists
const L_SHOULDER = 11;
const R_SHOULDER = 12;
const L_WRIST = 15;
const R_WRIST = 16;
const L_HIP = 23;
const R_HIP = 24;
const L_ANKLE = 27;
const R_ANKLE = 28;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

interface LandmarkSet {
  // Pixel-space (mapped via camera.width/height).
  pixels: Point[];
  // Normalized [0,1] originals (used for motion delta + baseline math).
  normalized: NormalizedLandmark[];
}

export class PoseTracker {
  private _frame: PoseFrame = emptyPose();
  private landmarker: PoseLandmarker | null = null;
  private unsubscribe: (() => void) | null = null;
  private running = false;

  // Calibration / derived-signal state.
  private baselineStartMs: number | null = null;
  private baselineSamples: number[] = []; // normalized torso-span samples
  private baselineDist: number | null = null;
  private ankleBaselineY: number | null = null;
  private ankleBaselineSamples: number[] = [];
  private jumpAirborneSinceMs: number | null = null;
  private jumpPrimed = false;

  private prevNormalized: NormalizedLandmark[] | null = null;
  private lastTsMs = 0;

  constructor(private readonly opts: PoseTrackerOptions) {}

  async load(): Promise<void> {
    if (this.landmarker != null) return;
    const fileset = await FilesetResolver.forVisionTasks(
      this.opts.wasmBasePath ?? DEFAULT_WASM_BASE,
    );
    this.landmarker = await PoseLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: this.opts.modelAssetPath ?? DEFAULT_MODEL_URL,
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      numPoses: 1,
      outputSegmentationMasks: false,
    });
  }

  start(): void {
    if (this.running) return;
    if (this.landmarker == null) {
      throw new Error('PoseTracker.start called before load()');
    }
    this.running = true;
    this.recalibrate();
    this.unsubscribe = this.opts.camera.onFrame(this.handleFrame);
  }

  stop(): void {
    this.running = false;
    if (this.unsubscribe != null) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    if (this.landmarker != null) {
      this.landmarker.close();
      this.landmarker = null;
    }
    this._frame = emptyPose();
    this.prevNormalized = null;
  }

  /** Reset squat / jump baselines. Re-runs the 1.5s capture window. */
  recalibrate(): void {
    this.baselineStartMs = null;
    this.baselineSamples = [];
    this.baselineDist = null;
    this.ankleBaselineY = null;
    this.ankleBaselineSamples = [];
    this.jumpAirborneSinceMs = null;
    this.jumpPrimed = false;
  }

  get lastFrame(): PoseFrame {
    return this._frame;
  }

  private handleFrame = (): void => {
    if (!this.running) return;
    const cam = this.opts.camera;
    const lm = this.landmarker;
    if (lm == null || !cam.isReady) return;

    const tsMs = performance.now();
    // detectForVideo demands monotonically increasing timestamps; rAF can
    // sometimes fire twice on the same ms boundary — bump by 1 if needed.
    const safeTs = tsMs <= this.lastTsMs ? this.lastTsMs + 1 : tsMs;
    this.lastTsMs = safeTs;

    let normalized: NormalizedLandmark[] | undefined;
    try {
      const result = lm.detectForVideo(cam.video, safeTs);
      normalized = result.landmarks[0];
    } catch {
      return;
    }

    if (normalized == null || normalized.length < 33) {
      // Preserve baseline state across drop-outs; emit empty frame.
      this._frame = emptyPose();
      this.prevNormalized = null;
      return;
    }

    const set = toLandmarkSet(normalized, cam.width, cam.height);
    const lShoulder = required(set.pixels, L_SHOULDER);
    const rShoulder = required(set.pixels, R_SHOULDER);
    const lHip = required(set.pixels, L_HIP);
    const rHip = required(set.pixels, R_HIP);

    const shoulderTilt = Math.atan2(
      rShoulder.y - lShoulder.y,
      rShoulder.x - lShoulder.x,
    );
    const shoulderWidthPx = distance(lShoulder, rShoulder);

    // Use normalized torso span so baseline is invariant to video resolution.
    const nLShoulder = required(set.normalized, L_SHOULDER);
    const nRShoulder = required(set.normalized, R_SHOULDER);
    const nLHip = required(set.normalized, L_HIP);
    const nRHip = required(set.normalized, R_HIP);
    const hipY = (nLHip.y + nRHip.y) / 2;
    const shoulderY = (nLShoulder.y + nRShoulder.y) / 2;
    const torsoSpan = hipY - shoulderY;

    const squatLevel = this.updateSquatBaselineAndLevel(torsoSpan, tsMs);
    const jumpEvent = this.detectJump(set, tsMs);
    const armRaiseLeft = isArmRaised(set.pixels, L_WRIST, L_SHOULDER, shoulderWidthPx);
    const armRaiseRight = isArmRaised(set.pixels, R_WRIST, R_SHOULDER, shoulderWidthPx);
    const motionDelta = computeMotionDelta(this.prevNormalized, set.normalized);

    // Hip-midpoint values are computed for parity with the spec wording even
    // though we use the normalized variant for level. Suppress lint:
    void lHip;
    void rHip;

    this._frame = {
      visible: true,
      landmarks: set.pixels,
      shoulderTilt,
      squatLevel,
      jumpEvent,
      armRaiseLeft,
      armRaiseRight,
      motionDelta,
    };

    this.prevNormalized = set.normalized;
  };

  private updateSquatBaselineAndLevel(torsoSpan: number, tsMs: number): number {
    if (this.baselineStartMs == null) {
      this.baselineStartMs = tsMs;
    }
    const elapsed = tsMs - this.baselineStartMs;
    if (this.baselineDist == null) {
      if (torsoSpan > 0) this.baselineSamples.push(torsoSpan);
      if (elapsed >= BASELINE_CAPTURE_MS && this.baselineSamples.length > 0) {
        this.baselineDist = median(this.baselineSamples);
      }
      return 0;
    }
    if (this.baselineDist <= 0) return 0;
    return clamp01((this.baselineDist - torsoSpan) / this.baselineDist);
  }

  private detectJump(set: LandmarkSet, tsMs: number): boolean {
    const nLAnkle = required(set.normalized, L_ANKLE);
    const nRAnkle = required(set.normalized, R_ANKLE);
    const nLShoulder = required(set.normalized, L_SHOULDER);
    const nRShoulder = required(set.normalized, R_SHOULDER);

    const shoulderWidthNorm = Math.abs(nRShoulder.x - nLShoulder.x);
    if (shoulderWidthNorm <= 0) return false;
    const riseThresholdNorm = JUMP_RISE_THRESHOLD_RATIO * shoulderWidthNorm;
    const ankleY = (nLAnkle.y + nRAnkle.y) / 2;

    if (this.baselineStartMs == null) return false;
    const elapsed = tsMs - this.baselineStartMs;
    if (this.ankleBaselineY == null) {
      if (ankleY > 0) this.ankleBaselineSamples.push(ankleY);
      if (elapsed >= BASELINE_CAPTURE_MS && this.ankleBaselineSamples.length > 0) {
        this.ankleBaselineY = median(this.ankleBaselineSamples);
      }
      return false;
    }

    const rise = this.ankleBaselineY - ankleY; // smaller y = higher = airborne
    const isAirborne = rise > riseThresholdNorm;

    if (isAirborne) {
      if (this.jumpAirborneSinceMs == null) {
        this.jumpAirborneSinceMs = tsMs;
        this.jumpPrimed = true;
      } else if (tsMs - this.jumpAirborneSinceMs > JUMP_MAX_AIRBORNE_MS) {
        // Held airborne too long (climbed on something) — cancel without firing.
        this.jumpPrimed = false;
      }
      return false;
    }
    if (this.jumpPrimed && this.jumpAirborneSinceMs != null) {
      const airborneMs = tsMs - this.jumpAirborneSinceMs;
      this.jumpAirborneSinceMs = null;
      this.jumpPrimed = false;
      return airborneMs > 0 && airborneMs <= JUMP_MAX_AIRBORNE_MS;
    }
    this.jumpAirborneSinceMs = null;
    return false;
  }
}

function toLandmarkSet(
  normalized: NormalizedLandmark[],
  width: number,
  height: number,
): LandmarkSet {
  // Mirror x for selfie-feel rendering (matches face/hands trackers).
  const pixels: Point[] = normalized.map((p) => ({
    x: (1 - p.x) * width,
    y: p.y * height,
  }));
  return { pixels, normalized };
}

function required<T>(arr: T[], idx: number): T {
  const v = arr[idx];
  if (v === undefined) {
    throw new Error(`PoseTracker: missing landmark index ${idx}`);
  }
  return v;
}

function distance(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function isArmRaised(
  landmarks: Point[],
  wristIdx: number,
  shoulderIdx: number,
  shoulderWidthPx: number,
): boolean {
  if (shoulderWidthPx <= 0) return false;
  const wrist = landmarks[wristIdx];
  const shoulder = landmarks[shoulderIdx];
  if (wrist === undefined || shoulder === undefined) return false;
  // Screen coords: smaller y = higher on screen. Wrist must be above shoulder
  // by ARM_RAISE_MARGIN_RATIO * shoulder_width.
  return wrist.y < shoulder.y - ARM_RAISE_MARGIN_RATIO * shoulderWidthPx;
}

function computeMotionDelta(
  prev: NormalizedLandmark[] | null,
  curr: NormalizedLandmark[],
): number {
  if (prev == null || prev.length !== curr.length) return 0;
  let sum = 0;
  for (let i = 0; i < curr.length; i++) {
    const c = curr[i];
    const p = prev[i];
    if (c === undefined || p === undefined) continue;
    sum += Math.abs(c.x - p.x) + Math.abs(c.y - p.y);
  }
  return sum;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    const a = sorted[mid - 1];
    const b = sorted[mid];
    if (a === undefined || b === undefined) return 0;
    return (a + b) / 2;
  }
  const v = sorted[mid];
  return v ?? 0;
}
